import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { AlertTriangle, BarChart3, FileText, Scale, Timer } from 'lucide-react';
import { Bar, BarChart, CartesianGrid, Cell, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis, Pie, PieChart } from 'recharts';
import api from '../services/api';

const StyledCard = styled(Card)(() => ({
  boxShadow: '0 0 2px 0 rgba(145, 158, 171, 0.2), 0 12px 24px -4px rgba(145, 158, 171, 0.12)',
  borderRadius: 16,
  overflow: 'hidden'
}));

const UI_COLORS = {
  indigo: '#4f46e5',
  green: '#16a34a',
  amber: '#f59e0b',
  red: '#ef4444',
  slate: '#334155'
};

const formatDateIso = (d) => {
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const addDays = (d, delta) => {
  const x = new Date(d);
  x.setDate(x.getDate() + delta);
  return x;
};

const getPresetRange = (preset) => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  if (preset === 'last_7_days') {
    return { from: formatDateIso(addDays(today, -6)), to: formatDateIso(today) };
  }
  if (preset === 'last_30_days') {
    return { from: formatDateIso(addDays(today, -29)), to: formatDateIso(today) };
  }
  if (preset === 'last_365_days') {
    return { from: formatDateIso(addDays(today, -364)), to: formatDateIso(today) };
  }
  if (preset === 'month_current') {
    const from = new Date(today.getFullYear(), today.getMonth(), 1);
    const to = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    return { from: formatDateIso(from), to: formatDateIso(to) };
  }
  if (preset === 'month_previous') {
    const from = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    const to = new Date(today.getFullYear(), today.getMonth(), 0);
    return { from: formatDateIso(from), to: formatDateIso(to) };
  }
  return { from: '', to: '' };
};

const presetLabel = (preset) => {
  const map = {
    last_7_days: 'Ultima settimana',
    last_30_days: 'Ultimo mese',
    last_365_days: 'Ultimo anno',
    month_current: 'Mese corrente',
    month_previous: 'Mese precedente',
    custom: 'Periodo'
  };
  return map[preset] || 'Periodo';
};

const formatDateIt = (value) => {
  const s = String(value || '').trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return '';
  return `${s.slice(8, 10)}/${s.slice(5, 7)}/${s.slice(0, 4)}`;
};

const formatTrendTick = (value) => {
  const s = String(value || '').trim();
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(s)) return s.slice(0, 5);
  return s;
};

const StatCard = ({ title, value, icon: Icon, accent }) => (
  <Paper
    elevation={0}
    variant="outlined"
    sx={{
      p: 2.2,
      borderRadius: 2,
      minHeight: 84,
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between'
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5 }}>
      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
        {title}
      </Typography>
      <Box sx={{ width: 34, height: 34, borderRadius: 2, bgcolor: `${accent}14`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: accent }}>
        <Icon size={18} />
      </Box>
    </Box>
    <Typography variant="h4" sx={{ fontWeight: 900, mt: 1 }}>
      {value}
    </Typography>
  </Paper>
);

const truncateLabel = (value, max = 26) => {
  const s = String(value || '').trim();
  if (!s) return '';
  if (s.length <= max) return s;
  return `${s.slice(0, Math.max(0, max - 1))}…`;
};

const normalizeRoleKey = (value) => {
  const key = String(value || '').trim().toLowerCase();
  if (key === 'commerciale') return 'commerciale';
  if (key === 'upsell') return 'upsell';
  if (key === 'contabile') return 'contabile';
  return '';
};

const roleLabel = (value) => {
  const map = { commerciale: 'Commerciale', upsell: 'Upsell', contabile: 'Contabile' };
  return map[normalizeRoleKey(value)] || value;
};

export default function RecessiKpi() {
  const currentYear = new Date().getFullYear();
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('overview');
  const [periodPreset, setPeriodPreset] = useState('last_30_days');
  const initialRange = useMemo(() => getPresetRange('last_30_days'), []);
  const [dateFrom, setDateFrom] = useState(initialRange.from);
  const [dateTo, setDateTo] = useState(initialRange.to);
  const [trendView, setTrendView] = useState('range');
  const [trendYear, setTrendYear] = useState(currentYear);
  const [data, setData] = useState(null);

  const yearOptions = useMemo(() => {
    const set = new Set([currentYear, currentYear - 1, currentYear - 2]);
    const fromYear = String(dateFrom || '').slice(0, 4);
    const toYear = String(dateTo || '').slice(0, 4);
    if (/^\d{4}$/.test(fromYear)) set.add(Number.parseInt(fromYear, 10));
    if (/^\d{4}$/.test(toYear)) set.add(Number.parseInt(toYear, 10));
    set.add(Number(trendYear));
    return Array.from(set).filter(Number.isFinite).sort((a, b) => b - a);
  }, [dateFrom, dateTo, trendYear, currentYear]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/recessi/kpi', {
        params: {
          from: dateFrom || undefined,
          to: dateTo || undefined,
          trend_mode: trendView === 'year' ? 'year' : undefined,
          year: trendView === 'year' ? trendYear : undefined
        }
      });
      setData(res.data || null);
    } catch (e) {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, trendView, trendYear]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const totals = useMemo(() => data?.totals || {}, [data]);
  const byIniziativa = useMemo(() => data?.by_iniziativa || {}, [data]);
  const byMotivazione = useMemo(() => (Array.isArray(data?.by_motivazione) ? data.by_motivazione : []), [data]);
  const byLegale = useMemo(() => (Array.isArray(data?.by_legale) ? data.by_legale : []), [data]);
  const byLegaleTotals = useMemo(() => data?.by_legale_totals || {}, [data]);
  const byOwnerRole = useMemo(() => data?.by_owner_role || {}, [data]);
  const byNegotiationCategory = useMemo(() => (Array.isArray(data?.by_negotiation_category) ? data.by_negotiation_category : []), [data]);
  const byNegotiationCategoryTotals = useMemo(() => data?.by_negotiation_category_totals || {}, [data]);
  const trend = useMemo(() => (Array.isArray(data?.trend) ? data.trend : []), [data]);
  const trendYearRows = useMemo(() => (Array.isArray(data?.trend_year) ? data.trend_year : []), [data]);

  const subtitle = useMemo(() => {
    const rangeLabel = dateFrom && dateTo ? `${formatDateIt(dateFrom)} → ${formatDateIt(dateTo)}` : '—';
    return `${presetLabel(periodPreset)} • ${rangeLabel}`;
  }, [periodPreset, dateFrom, dateTo]);

  const tardiviCardValue = useMemo(() => {
    const tardivi = Number(totals.recessi_tardivi_cliente || 0);
    const tot = Number(totals.recessi_totale_cliente || 0);
    return `${tardivi}/${tot}`;
  }, [totals]);

  const iniziativaChartData = useMemo(
    () => [
      { key: 'cliente', name: 'Cliente', value: Number(byIniziativa.cliente || 0), color: UI_COLORS.green },
      { key: 'studio', name: 'Studio', value: Number(byIniziativa.studio || 0), color: UI_COLORS.amber }
    ],
    [byIniziativa]
  );

  const motivazioneChartData = useMemo(
    () =>
      (byMotivazione || []).map((m) => ({
        key: m.codice || String(m.id_motivazione),
        label: m.descrizione || m.codice || '-',
        value: Number(m.recessi_count || 0)
      })),
    [byMotivazione]
  );

  const trendChartData = useMemo(
    () =>
      (trend || []).map((r) => ({
        label: r.label || r.period,
        recessi_totali: Number(r.recessi_totali || 0),
        recessi_aperti: Number(r.recessi_aperti || 0)
      })),
    [trend]
  );

  const trendYearChartData = useMemo(
    () =>
      (trendYearRows || []).map((r) => ({
        label: r.label || r.period,
        recessi_totali: Number(r.recessi_totali || 0),
        recessi_aperti: Number(r.recessi_aperti || 0)
      })),
    [trendYearRows]
  );

  const activeTrendData = trendView === 'year' ? trendYearChartData : trendChartData;

  const legaleChartData = useMemo(() => {
    const rows = (byLegale || [])
      .map((r) => ({
        key: r.legal_user_id == null ? 'unassigned' : String(r.legal_user_id),
        label: r.legal_name || (r.legal_user_id == null ? 'Senza legale' : `ID ${r.legal_user_id}`),
        value: Number(r.recessi_count || 0)
      }))
      .filter((r) => r.value > 0);
    rows.sort((a, b) => b.value - a.value || String(a.label).localeCompare(String(b.label)));
    const top = rows.slice(0, 12);
    const rest = rows.slice(12);
    const otherSum = rest.reduce((acc, r) => acc + Number(r.value || 0), 0);
    if (otherSum > 0) top.push({ key: 'other', label: 'Altri', value: otherSum });
    return top;
  }, [byLegale]);

  const ownerChartData = useCallback(
    (roleKey) => {
      const role = normalizeRoleKey(roleKey);
      const rows = Array.isArray(byOwnerRole?.[role]?.rows) ? byOwnerRole[role].rows : [];
      const dataRows = rows
        .filter((r) => String(r.owner_name || '') !== 'Senza referente')
        .map((r) => ({
          key: r.owner_id == null ? 'unassigned' : String(r.owner_id),
          label: r.owner_name || (r.owner_id == null ? 'Senza referente' : `ID ${r.owner_id}`),
          value: Number(r.recessi_count || 0)
        }))
        .filter((r) => r.value > 0);
      dataRows.sort((a, b) => b.value - a.value || String(a.label).localeCompare(String(b.label)));
      const top = dataRows.slice(0, 12);
      const rest = dataRows.slice(12);
      const otherSum = rest.reduce((acc, r) => acc + Number(r.value || 0), 0);
      if (otherSum > 0) top.push({ key: 'other', label: 'Altri', value: otherSum });
      const unassigned = rows.find((r) => String(r.owner_name || '') === 'Senza referente');
      if (unassigned && Number(unassigned.recessi_count || 0) > 0) {
        top.unshift({ key: 'unassigned', label: 'Senza referente', value: Number(unassigned.recessi_count || 0) });
      }
      return top;
    },
    [byOwnerRole]
  );

  const ownerChartDataActive = useMemo(() => ownerChartData(tab), [ownerChartData, tab]);

  const negotiationCategoryChartData = useMemo(() => {
    const rows = (byNegotiationCategory || [])
      .map((r) => ({
        key: String(r.category_name || 'unassigned'),
        label: r.category_name || '—',
        value: Number(r.recessi_count || 0)
      }))
      .filter((r) => r.value > 0);
    rows.sort((a, b) => b.value - a.value || String(a.label).localeCompare(String(b.label)));
    const top = rows.slice(0, 12);
    const rest = rows.slice(12);
    const otherSum = rest.reduce((acc, r) => acc + Number(r.value || 0), 0);
    if (otherSum > 0) top.push({ key: 'other', label: 'Altri', value: otherSum });
    return top;
  }, [byNegotiationCategory]);

  const barColors = [UI_COLORS.green, '#8b5cf6', '#2563eb', '#f97316', '#0ea5e9', '#a16207', '#334155', '#16a34a', '#b91c1c'];

  return (
    <Box sx={{ p: 3 }}>
      <StyledCard>
        <CardContent>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: 2,
              alignItems: { xs: 'stretch', md: 'flex-start' },
              flexWrap: { xs: 'wrap', md: 'nowrap' },
              mb: 3
            }}
          >
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <Box
                sx={{
                  width: 42,
                  height: 42,
                  borderRadius: '50%',
                  bgcolor: `${UI_COLORS.slate}12`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <BarChart3 size={20} color={UI_COLORS.slate} />
              </Box>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 900, lineHeight: 1.15 }}>
                  Dashboard KPI recessi
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {subtitle}
                </Typography>
              </Box>
            </Box>

            <Box
              sx={{
                display: 'flex',
                gap: 1,
                alignItems: 'center',
                flexWrap: { xs: 'wrap', lg: 'nowrap' },
                justifyContent: { xs: 'flex-start', md: 'flex-end' },
                width: { xs: '100%', md: 'auto' }
              }}
            >
              <FormControl size="small" sx={{ minWidth: 160, flex: { xs: '1 1 160px', lg: '0 0 auto' } }}>
                <InputLabel>Intervallo</InputLabel>
                <Select
                  label="Intervallo"
                  value={periodPreset}
                  onChange={(e) => {
                    const next = String(e.target.value || '');
                    setPeriodPreset(next);
                    const range = getPresetRange(next);
                    if (range.from && range.to) {
                      setDateFrom(range.from);
                      setDateTo(range.to);
                    }
                  }}
                >
                  <MenuItem value="last_7_days">Ultima settimana</MenuItem>
                  <MenuItem value="last_30_days">Ultimo mese</MenuItem>
                  <MenuItem value="last_365_days">Ultimo anno</MenuItem>
                  <MenuItem value="month_current">Mese corrente</MenuItem>
                  <MenuItem value="month_previous">Mese precedente</MenuItem>
                  <MenuItem value="custom">Personalizzato</MenuItem>
                </Select>
              </FormControl>
              <TextField
                size="small"
                type="date"
                label="Da"
                value={dateFrom}
                onChange={(e) => {
                  setPeriodPreset('custom');
                  setDateFrom(String(e.target.value || ''));
                }}
                InputLabelProps={{ shrink: true }}
                sx={{ minWidth: 140, flex: { xs: '1 1 140px', lg: '0 0 auto' } }}
              />
              <TextField
                size="small"
                type="date"
                label="A"
                value={dateTo}
                onChange={(e) => {
                  setPeriodPreset('custom');
                  setDateTo(String(e.target.value || ''));
                }}
                InputLabelProps={{ shrink: true }}
                sx={{ minWidth: 140, flex: { xs: '1 1 140px', lg: '0 0 auto' } }}
              />
            </Box>
          </Box>

          {loading ? (
            <Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box>
              <Tabs
                value={tab}
                onChange={(_, v) => setTab(v)}
                sx={{
                  mb: 2.5,
                  minHeight: 44,
                  '& .MuiTab-root': { textTransform: 'none', fontWeight: 800, minHeight: 44 }
                }}
              >
                <Tab value="overview" label="Panoramica" />
                <Tab value="trend" label="Trend" />
                <Tab value="legali" label="Legali" />
                <Tab value="commerciale" label="Commerciale" />
                <Tab value="upsell" label="Upsell" />
                <Tab value="contabile" label="Contabile" />
                <Tab value="tipologia" label="Tipologia Pratica" />
              </Tabs>

              {tab === 'overview' ? (
                <Box>
                  <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={12} sm={6} md={3}>
                      <StatCard title="Recessi aperti" value={Number(totals.recessi_aperti || 0)} icon={Timer} accent={UI_COLORS.indigo} />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <StatCard title="Recessi tardivi (cliente)" value={tardiviCardValue} icon={AlertTriangle} accent={UI_COLORS.amber} />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <StatCard title="Rinunce studio" value={Number(totals.rinunce_studio || 0)} icon={Scale} accent={UI_COLORS.slate} />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <StatCard title="Relazioni in bozza" value={Number(totals.relazioni_bozza || 0)} icon={FileText} accent={UI_COLORS.red} />
                    </Grid>
                  </Grid>

                  <Grid container spacing={2}>
                    <Grid item xs={12} md={5}>
                      <Paper elevation={0} variant="outlined" sx={{ p: 2.2, borderRadius: 2, height: '100%' }}>
                        <Typography sx={{ fontWeight: 900, mb: 1.5 }}>Iniziativa del recesso</Typography>
                        <Box sx={{ width: '100%', height: 300 }}>
                          <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                              <Pie
                                data={iniziativaChartData}
                                dataKey="value"
                                nameKey="name"
                                innerRadius={70}
                                outerRadius={110}
                                paddingAngle={2}
                              >
                                {iniziativaChartData.map((entry) => (
                                  <Cell key={entry.key} fill={entry.color} />
                                ))}
                              </Pie>
                              <Tooltip />
                              <Legend />
                            </PieChart>
                          </ResponsiveContainer>
                        </Box>
                      </Paper>
                    </Grid>

                    <Grid item xs={12} md={7}>
                      <Paper elevation={0} variant="outlined" sx={{ p: 2.2, borderRadius: 2, height: '100%' }}>
                        <Typography sx={{ fontWeight: 900, mb: 1.5 }}>Recessi per motivazione</Typography>
                        <Box sx={{ width: '100%', height: 300 }}>
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={motivazioneChartData} layout="vertical" margin={{ left: 24, right: 12, top: 8, bottom: 8 }}>
                              <XAxis type="number" allowDecimals={false} tick={{ fontSize: 12 }} />
                              <YAxis
                                type="category"
                                dataKey="label"
                                width={220}
                                tick={{ fontSize: 12 }}
                                tickFormatter={(v) => truncateLabel(v, 34)}
                              />
                              <Tooltip formatter={(v) => [v, 'Recessi']} labelFormatter={(v) => String(v || '')} />
                              <Bar dataKey="value" radius={[6, 6, 6, 6]}>
                                {motivazioneChartData.map((_, idx) => (
                                  <Cell key={motivazioneChartData[idx]?.key || idx} fill={barColors[idx % barColors.length]} />
                                ))}
                              </Bar>
                            </BarChart>
                          </ResponsiveContainer>
                        </Box>
                      </Paper>
                    </Grid>
                  </Grid>
                </Box>
              ) : null}

              {tab === 'trend' ? (
                <Paper elevation={0} variant="outlined" sx={{ p: 2.2, borderRadius: 2 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      gap: 1.5,
                      alignItems: { xs: 'stretch', md: 'center' },
                      flexWrap: { xs: 'wrap', md: 'nowrap' },
                      mb: 1.5
                    }}
                  >
                    <Typography sx={{ fontWeight: 900 }}>
                      {trendView === 'year' ? `Trend mensile (Gen–Dic) • ${trendYear}` : 'Trend recessi'}
                    </Typography>

                    <Box
                      sx={{
                        display: 'flex',
                        gap: 1,
                        alignItems: 'center',
                        flexWrap: { xs: 'wrap', md: 'nowrap' },
                        width: { xs: '100%', md: 'auto' },
                        justifyContent: { xs: 'flex-start', md: 'flex-end' }
                      }}
                    >
                      <FormControl size="small" sx={{ minWidth: 190, flex: { xs: '1 1 190px', md: '0 0 auto' } }}>
                        <InputLabel>Vista</InputLabel>
                        <Select
                          label="Vista"
                          value={trendView}
                          onChange={(e) => {
                            const next = String(e.target.value || 'range');
                            setTrendView(next);
                            if (next === 'year') {
                              const y = Number.parseInt(String(dateFrom || '').slice(0, 4), 10);
                              if (Number.isFinite(y)) setTrendYear(y);
                            }
                          }}
                        >
                          <MenuItem value="range">Periodo selezionato</MenuItem>
                          <MenuItem value="year">Anno (gen–dic)</MenuItem>
                        </Select>
                      </FormControl>

                      {trendView === 'year' ? (
                        <FormControl size="small" sx={{ minWidth: 120, flex: { xs: '1 1 120px', md: '0 0 auto' } }}>
                          <InputLabel>Anno</InputLabel>
                          <Select
                            label="Anno"
                            value={trendYear}
                            onChange={(e) => setTrendYear(Number.parseInt(String(e.target.value || currentYear), 10))}
                          >
                            {yearOptions.map((y) => (
                              <MenuItem key={y} value={y}>
                                {y}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      ) : null}
                    </Box>
                  </Box>

                  <Box sx={{ height: 320 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={activeTrendData} margin={{ top: 8, right: 24, left: 0, bottom: 8 }}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                        <XAxis
                          dataKey="label"
                          tick={{ fontSize: 12 }}
                          tickFormatter={trendView === 'year' ? undefined : formatTrendTick}
                          interval={trendView === 'year' ? 0 : 'preserveStartEnd'}
                          minTickGap={trendView === 'year' ? undefined : 18}
                          angle={trendView === 'year' ? 0 : -35}
                          textAnchor={trendView === 'year' ? 'middle' : 'end'}
                          height={trendView === 'year' ? 34 : 52}
                        />
                        <YAxis allowDecimals={false} />
                        <Tooltip />
                        <Legend />
                        <Bar
                          dataKey="recessi_totali"
                          name="Recessi"
                          fill={UI_COLORS.indigo}
                          radius={[6, 6, 0, 0]}
                          maxBarSize={trendView === 'year' ? 34 : 18}
                        />
                        <Bar
                          dataKey="recessi_aperti"
                          name="Aperti"
                          fill={UI_COLORS.amber}
                          radius={[6, 6, 0, 0]}
                          maxBarSize={trendView === 'year' ? 34 : 18}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </Box>
                </Paper>
              ) : null}

              {tab === 'legali' ? (
                <Paper elevation={0} variant="outlined" sx={{ p: 2.2, borderRadius: 2 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      gap: 1.5,
                      alignItems: { xs: 'stretch', md: 'center' },
                      flexWrap: { xs: 'wrap', md: 'nowrap' },
                      mb: 1.5
                    }}
                  >
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                      <Typography sx={{ fontWeight: 900 }}>Distribuzione recessi per legale</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 700 }}>
                        Recessi (con dossier): {Number(byLegaleTotals.recessi_distinct_with_dossier || 0)} • Attribuzioni: {Number(byLegaleTotals.recessi_assigned_total || 0)} • Senza legale: {Number(byLegaleTotals.recessi_without_legale || 0)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 700 }}>
                        Clienti unici: {Number(byLegaleTotals.customers_distinct_with_dossier || 0)} • Clienti assegnati: {Number(byLegaleTotals.customers_distinct_assigned || 0)} • Clienti senza legale: {Number(byLegaleTotals.customers_without_legale || 0)}
                      </Typography>
                    </Box>
                  </Box>

                  <Grid container spacing={2}>
                    <Grid item xs={12} md={7}>
                      <Box sx={{ width: '100%', height: 360 }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={legaleChartData} layout="vertical" margin={{ left: 24, right: 12, top: 14, bottom: 14 }}>
                            <XAxis type="number" allowDecimals={false} tick={{ fontSize: 12 }} />
                            <YAxis
                              type="category"
                              dataKey="label"
                              width={240}
                              interval={0}
                              tick={{ fontSize: 12 }}
                              tickFormatter={(v) => truncateLabel(v, 34)}
                              tickMargin={6}
                              padding={{ top: 10, bottom: 10 }}
                            />
                            <Tooltip formatter={(v) => [v, 'Recessi']} labelFormatter={(v) => String(v || '')} />
                            <Bar dataKey="value" radius={[6, 6, 6, 6]}>
                              {legaleChartData.map((_, idx) => (
                                <Cell key={legaleChartData[idx]?.key || idx} fill={barColors[idx % barColors.length]} />
                              ))}
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      </Box>
                    </Grid>

                    <Grid item xs={12} md={5}>
                      <TableContainer component={Paper} elevation={0} variant="outlined" sx={{ borderRadius: 2 }}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 900 }}>Legale</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 900 }}>
                                Clienti
                              </TableCell>
                              <TableCell align="right" sx={{ fontWeight: 900 }}>
                                Recessi
                              </TableCell>
                              <TableCell align="right" sx={{ fontWeight: 900 }}>
                                %
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {(byLegale || []).map((r) => (
                              <TableRow key={r.legal_user_id == null ? 'unassigned' : String(r.legal_user_id)} hover>
                                <TableCell sx={{ maxWidth: 240 }}>
                                  <Typography variant="body2" sx={{ fontWeight: 800 }} noWrap title={r.legal_name || ''}>
                                    {r.legal_name || (r.legal_user_id == null ? 'Senza legale' : `ID ${r.legal_user_id}`)}
                                  </Typography>
                                </TableCell>
                                <TableCell align="right" sx={{ fontWeight: 900 }}>
                                  {Number(r.customers_count || 0)}
                                </TableCell>
                                <TableCell align="right" sx={{ fontWeight: 900 }}>
                                  {Number(r.recessi_count || 0)}
                                </TableCell>
                                <TableCell align="right" sx={{ fontWeight: 800, color: 'text.secondary' }}>
                                  {r.recessi_percent == null ? '—' : `${Number(r.recessi_percent).toFixed(2)}%`}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Grid>
                  </Grid>
                </Paper>
              ) : null}

              {tab === 'commerciale' || tab === 'upsell' || tab === 'contabile' ? (
                <Paper elevation={0} variant="outlined" sx={{ p: 2.2, borderRadius: 2 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      gap: 1.5,
                      alignItems: { xs: 'stretch', md: 'center' },
                      flexWrap: { xs: 'wrap', md: 'nowrap' },
                      mb: 1.5
                    }}
                  >
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                      <Typography sx={{ fontWeight: 900 }}>Distribuzione recessi per referente {roleLabel(tab).toLowerCase()}</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 700 }}>
                        Recessi (con dossier): {Number(byOwnerRole?.[tab]?.totals?.recessi_distinct_with_dossier || 0)} • Attribuzioni: {Number(byOwnerRole?.[tab]?.totals?.recessi_assigned_total || 0)} • Senza referente: {Number(byOwnerRole?.[tab]?.totals?.recessi_without_owner || 0)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 700 }}>
                        Clienti unici: {Number(byOwnerRole?.[tab]?.totals?.customers_distinct_with_dossier || 0)} • Clienti assegnati: {Number(byOwnerRole?.[tab]?.totals?.customers_distinct_assigned || 0)} • Clienti senza referente: {Number(byOwnerRole?.[tab]?.totals?.customers_without_owner || 0)}
                      </Typography>
                    </Box>
                  </Box>

                  <Grid container spacing={2}>
                    <Grid item xs={12} md={7}>
                      <Box sx={{ width: '100%', height: 360 }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={ownerChartDataActive} layout="vertical" margin={{ left: 24, right: 12, top: 14, bottom: 14 }}>
                            <XAxis type="number" allowDecimals={false} tick={{ fontSize: 12 }} />
                            <YAxis
                              type="category"
                              dataKey="label"
                              width={240}
                              interval={0}
                              tick={{ fontSize: 12 }}
                              tickFormatter={(v) => truncateLabel(v, 34)}
                              tickMargin={6}
                              padding={{ top: 10, bottom: 10 }}
                            />
                            <Tooltip formatter={(v) => [v, 'Recessi']} labelFormatter={(v) => String(v || '')} />
                            <Bar dataKey="value" radius={[6, 6, 6, 6]}>
                              {ownerChartDataActive.map((_, idx) => (
                                <Cell key={ownerChartDataActive[idx]?.key || idx} fill={barColors[idx % barColors.length]} />
                              ))}
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      </Box>
                    </Grid>

                    <Grid item xs={12} md={5}>
                      <TableContainer component={Paper} elevation={0} variant="outlined" sx={{ borderRadius: 2 }}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 900 }}>Referente</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 900 }}>
                                Clienti
                              </TableCell>
                              <TableCell align="right" sx={{ fontWeight: 900 }}>
                                Recessi
                              </TableCell>
                              <TableCell align="right" sx={{ fontWeight: 900 }}>
                                %
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {(Array.isArray(byOwnerRole?.[tab]?.rows) ? byOwnerRole[tab].rows : []).map((r) => (
                              <TableRow key={r.owner_id == null ? 'unassigned' : String(r.owner_id)} hover>
                                <TableCell sx={{ maxWidth: 240 }}>
                                  <Typography variant="body2" sx={{ fontWeight: 800 }} noWrap title={r.owner_name || ''}>
                                    {r.owner_name || (r.owner_id == null ? 'Senza referente' : `ID ${r.owner_id}`)}
                                  </Typography>
                                </TableCell>
                                <TableCell align="right" sx={{ fontWeight: 900 }}>
                                  {Number(r.customers_count || 0)}
                                </TableCell>
                                <TableCell align="right" sx={{ fontWeight: 900 }}>
                                  {Number(r.recessi_count || 0)}
                                </TableCell>
                                <TableCell align="right" sx={{ fontWeight: 800, color: 'text.secondary' }}>
                                  {r.recessi_percent == null ? '—' : `${Number(r.recessi_percent).toFixed(2)}%`}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Grid>
                  </Grid>
                </Paper>
              ) : null}

              {tab === 'tipologia' ? (
                <Paper elevation={0} variant="outlined" sx={{ p: 2.2, borderRadius: 2 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      gap: 1.5,
                      alignItems: { xs: 'stretch', md: 'center' },
                      flexWrap: { xs: 'wrap', md: 'nowrap' },
                      mb: 1.5
                    }}
                  >
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                      <Typography sx={{ fontWeight: 900 }}>Distribuzione recessi per tipologia pratica</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 700 }}>
                        Recessi (con dossier): {Number(byNegotiationCategoryTotals.recessi_distinct_with_dossier || 0)} • Attribuzioni: {Number(byNegotiationCategoryTotals.recessi_assigned_total || 0)} • Senza tipologia: {Number(byNegotiationCategoryTotals.recessi_without_category || 0)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 700 }}>
                        Clienti unici: {Number(byNegotiationCategoryTotals.customers_distinct_with_dossier || 0)} • Clienti assegnati: {Number(byNegotiationCategoryTotals.customers_distinct_assigned || 0)} • Clienti senza tipologia: {Number(byNegotiationCategoryTotals.customers_without_category || 0)}
                      </Typography>
                    </Box>
                  </Box>

                  <Grid container spacing={2}>
                    <Grid item xs={12} md={7}>
                      <Box sx={{ width: '100%', height: 360 }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={negotiationCategoryChartData} layout="vertical" margin={{ left: 24, right: 12, top: 14, bottom: 14 }}>
                            <XAxis type="number" allowDecimals={false} tick={{ fontSize: 12 }} />
                            <YAxis
                              type="category"
                              dataKey="label"
                              width={240}
                              interval={0}
                              tick={{ fontSize: 12 }}
                              tickFormatter={(v) => truncateLabel(v, 34)}
                              tickMargin={6}
                              padding={{ top: 10, bottom: 10 }}
                            />
                            <Tooltip formatter={(v) => [v, 'Recessi']} labelFormatter={(v) => String(v || '')} />
                            <Bar dataKey="value" radius={[6, 6, 6, 6]}>
                              {negotiationCategoryChartData.map((_, idx) => (
                                <Cell key={negotiationCategoryChartData[idx]?.key || idx} fill={barColors[idx % barColors.length]} />
                              ))}
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      </Box>
                    </Grid>

                    <Grid item xs={12} md={5}>
                      <TableContainer component={Paper} elevation={0} variant="outlined" sx={{ borderRadius: 2 }}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 900 }}>Tipologia</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 900 }}>
                                Clienti
                              </TableCell>
                              <TableCell align="right" sx={{ fontWeight: 900 }}>
                                Recessi
                              </TableCell>
                              <TableCell align="right" sx={{ fontWeight: 900 }}>
                                %
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {(byNegotiationCategory || []).map((r) => (
                              <TableRow key={String(r.category_name || 'unassigned')} hover>
                                <TableCell sx={{ maxWidth: 240 }}>
                                  <Typography variant="body2" sx={{ fontWeight: 800 }} noWrap title={r.category_name || ''}>
                                    {r.category_name || '—'}
                                  </Typography>
                                </TableCell>
                                <TableCell align="right" sx={{ fontWeight: 900 }}>
                                  {Number(r.customers_count || 0)}
                                </TableCell>
                                <TableCell align="right" sx={{ fontWeight: 900 }}>
                                  {Number(r.recessi_count || 0)}
                                </TableCell>
                                <TableCell align="right" sx={{ fontWeight: 800, color: 'text.secondary' }}>
                                  {r.recessi_percent == null ? '—' : `${Number(r.recessi_percent).toFixed(2)}%`}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Grid>
                  </Grid>
                </Paper>
              ) : null}
            </Box>
          )}
        </CardContent>
      </StyledCard>
    </Box>
  );
}
